
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useTokens } from "@/hooks/use-tokens";
import { useAdminStatus } from "@/hooks/use-admin-status";
import { toast } from "sonner";
import type { Message } from "./medical-assistant/types";
import { useVoiceSettings } from "./medical-assistant/use-voice-settings";
import { useAudioPlayback } from "./medical-assistant/use-audio-playback";
import { useVoiceRecording } from "./medical-assistant/use-voice-recording";
import { useImageHandling } from "./medical-assistant/use-image-handling";
import { useMessageHandling } from "./medical-assistant/use-message-handling";
import { useFormSubmission } from "./medical-assistant/use-form-submission";

export type { Message };

export function useMedicalAssistant() {
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { user, isAuthenticated } = useAuth();
  const { hasEnoughTokens } = useTokens();
  const { isAdmin } = useAdminStatus();

  const voiceSettings = useVoiceSettings();
  const audioPlayback = useAudioPlayback();
  const voiceRecording = useVoiceRecording();
  const imageHandling = useImageHandling();
  const messageHandling = useMessageHandling();

  const formSubmission = useFormSubmission({
    voiceRecording,
    voiceSettings,
    audioPlayback,
    imageHandling,
    addMessage: messageHandling.addMessage,
    setIsLoading
  });

  // Configuration du callback pour l'audio prêt avec gestion améliorée
  useEffect(() => {
    voiceRecording.setOnAudioReady(() => {
      console.log("Audio is ready, auto-submitting immediately");
      
      const form = document.querySelector('form');
      if (form) {
        console.log("Found form, triggering submit");
        const submitEvent = new Event('submit', { 
          bubbles: true, 
          cancelable: true 
        });
        form.dispatchEvent(submitEvent);
      } else {
        console.error("No form found for auto-submit");
      }
    });
  }, [voiceRecording.setOnAudioReady]);

  const reset = () => {
    messageHandling.reset();
    imageHandling.reset();
  };

  const startRecording = async () => {
    if (!isAdmin && !hasEnoughTokens()) {
      toast.error("🎁 Votre token est insuffisant. Veuillez faire un don via Ko-fi pour obtenir plus de tokens.");
      return;
    }
    await voiceRecording.startRecording();
  };

  const stopRecordingAndSubmit = () => {
    console.log("stopRecordingAndSubmit called - stopping recording with auto-submit");
    voiceRecording.stopRecordingAndSubmit();
  };

  const toggleVoiceModal = (open?: boolean) => {
    setModalOpen(open ?? !modalOpen);
  };

  useEffect(() => {
    return () => {
      audioPlayback.stopAudio();
    };
  }, [audioPlayback.stopAudio]);

  return {
    messages: messageHandling.messages,
    isLoading,
    isRecording: voiceRecording.isRecording,
    isProcessingAudio: voiceRecording.isProcessingAudio,
    isSpeaking: audioPlayback.isSpeaking,
    selectedImage: imageHandling.selectedImage,
    imageUrl: imageHandling.imageUrl,
    // Correction: accès aux settings via voiceSettings.settings
    currentLanguage: voiceSettings.settings.preferredLanguage,
    continuousMode: voiceSettings.settings.continuousMode,
    autoVoiceResponse: voiceSettings.settings.autoVoiceResponse,
    voiceOnlyMode: voiceSettings.settings.voiceOnlyMode,
    useElevenLabs: voiceSettings.settings.useElevenLabs,
    modalOpen,
    // Propriété volumeLevel supprimée car elle n'existe plus dans audioPlayback
    imageAnalysisMode: imageHandling.imageAnalysisMode,
    handleSubmit: formSubmission.handleSubmit,
    reset,
    handleImageChange: imageHandling.handleImageChange,
    setImageUrl: imageHandling.setImageUrl,
    startRecording,
    stopRecording: voiceRecording.stopRecording,
    stopRecordingAndSubmit,
    saveVoiceRecording: voiceRecording.saveVoiceRecording,
    // Correction: utilisation des bonnes méthodes depuis voiceSettings
    setCurrentLanguage: voiceSettings.setLanguage,
    setContinuousMode: voiceSettings.toggleContinuousMode,
    setAutoVoiceResponse: voiceSettings.toggleAutoVoiceResponse,
    setVoiceOnlyMode: voiceSettings.toggleVoiceOnlyMode,
    setUseElevenLabs: voiceSettings.toggleElevenLabs,
    toggleVoiceModal,
    // Propriété setVolumeLevel supprimée car elle n'existe plus
    setImageAnalysisMode: imageHandling.setImageAnalysisMode,
    stopAudio: audioPlayback.stopAudio
  };
}
